import React from "react";
import PropTypes from "prop-types";
import { useDrag } from "react-dnd";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import { styled } from "@mui/material/styles";

import { ICONS } from "~/constants/materials";
import { MATERIAL_ITEM_TYPE } from "~/constants/dnd-types";
import EventBus from "~/libs/eventBus";
import { materialType } from "~/constants/prop-types";

const Preview = styled("li")(({ theme }) => ({
  "& + &": {
    borderTop: "1px solid " + theme.palette.grayscale[3]
  },
  "li + &": {
    borderTop: "1px solid " + theme.palette.grayscale[3]
  },
  "& + li": {
    borderTop: "1px solid " + theme.palette.grayscale[3]
  }
}));

const MaterialWrapper = styled("div")(({ theme }) => ({
  cursor: "pointer",
  "& + &": {
    borderTop: "1px solid " + theme.palette.grayscale[3]
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}));

const MaterialItem = styled(ListItemButton)(({ theme }) => ({
  "&.MuiListItemButton-root": {
    padding: theme.spacing(1),
    "& + &": {
      borderTop: "1px solid " + theme.palette.grayscale[3]
    }
  },
  "&:focus-visible": {
    outline: theme.outline(),
    outlineOffset: "-2px"
  }
}));

const MaterialIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 0,
  padding: theme.spacing(1)
}));

const MaterialText = styled(ListItemText)(({ theme }) => ({
  "&.MuiListItemText-root": {
    hyphens: "auto"
  },
  "&.MuiListItemText-primary": {
    fontSize: theme.typography.pxToRem(16)
  },
  "&.MuiListItemText-secondary": {
    fontSize: theme.typography.pxToRem(16)
  }
}));

const teachingMaterialIconStyles = {
  minHeight: "24px",
  minWidth: "24px"
};

function DragAndDropEntry({ material, onHandleClick, isEditMode }) {
  const [{}, dragSource, dragPreview] = useDrag({
    item: {
      type: MATERIAL_ITEM_TYPE,
      _id: material._id,
      _rev: material._rev,
      position: material.position,
      comingFromSidebar: true
    },
    canDrag: () => isEditMode,
    begin: () => EventBus.dispatch("onDragBegin"),
    end: () => EventBus.dispatch("onDragEnd")
  });

  const Icon = ICONS[material.materialType];

  function getIconStyle() {
    const cursor = isEditMode ? "move" : "initial";
    return { ...teachingMaterialIconStyles, cursor };
  }

  return (
    <Preview ref={dragPreview}>
      <MaterialWrapper key={material._id}>
        <MaterialItem component="div" onClick={onHandleClick}>
          <MaterialIcon ref={dragSource}>
            <Icon style={getIconStyle()} />
          </MaterialIcon>
          <MaterialText primary={material.title} secondary={material.description} />
        </MaterialItem>
      </MaterialWrapper>
    </Preview>
  );
}

DragAndDropEntry.propTypes = {
  material: materialType,
  onHandleClick: PropTypes.func,
  isEditMode: PropTypes.bool.isRequired
};

export default DragAndDropEntry;
